import React from 'react';
import { Button, Input, Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, ScrollArea, } from '@cargomatic/ui';
import { Bot, User, Trash, Repeat, Copy, X, Mail, Paperclip, ArrowUp, Sparkle, } from 'lucide-react';
import { useAIChatWithQuery } from './useAIAssistantQuery';
import { TypingIndicator } from './TypingIndicator';
import SessionStore from 'stores/SessionStore';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// Helper function to detect CSV content
const detectCSV = (content) => {
    const lines = content.split('\n');
    // Check if content looks like CSV (has headers and data rows)
    if (lines.length >= 2) {
        const firstLine = lines[0].trim();
        const secondLine = lines[1].trim();
        // Check if first line looks like headers (mostly letters/spaces with commas)
        const headerPattern = /^[A-Za-z\s,]+$/;
        // Check if second line looks like data (numbers, letters, commas)
        const dataPattern = /^[A-Za-z0-9\s,]+$/;
        if (headerPattern.test(firstLine) &&
            dataPattern.test(secondLine) &&
            firstLine.includes(',')) {
            // Extract the CSV portion
            const csvStart = content.indexOf(firstLine);
            const csvEnd = content.lastIndexOf('\n') + 1;
            return content.substring(csvStart, csvEnd).trim();
        }
    }
    return null;
};
// Helper function to copy text to clipboard
const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
    }
    catch (_err) {
        console.error(_err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    }
};
// Helper function to convert file to base64
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result;
            // Remove the data URL prefix (e.g., "data:image/png;base64,")
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = (error) => reject(error);
    });
};
// Helper function to create file preview URL
const createFilePreview = (file) => {
    return URL.createObjectURL(file);
};
export function AIChatOverlay({ isOpen, onClose, shipmentId, }) {
    const [uploadedFiles, setUploadedFiles] = React.useState([]);
    const fileInputRef = React.useRef(null);
    const { messages, input, isLoading, scrollAreaRef, handleSubmitWithFiles, handleInputChange, clearMessages, retryMessage, initOAuth, } = useAIChatWithQuery(isOpen, shipmentId);
    const handleOpenChange = (open) => {
        if (!open) {
            onClose();
        }
    };
    // Auto-scroll to latest message
    React.useEffect(() => {
        if (scrollAreaRef === null || scrollAreaRef === void 0 ? void 0 : scrollAreaRef.current) {
            const scrollElement = scrollAreaRef.current;
            scrollElement.scrollTop = scrollElement.scrollHeight;
        }
    }, [messages, isLoading]);
    const handleCopyCSV = async (content) => {
        const csvData = detectCSV(content);
        if (csvData) {
            await copyToClipboard(csvData);
        }
    };
    const handleFileUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0)
            return;
        const newFiles = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            try {
                const base64 = await fileToBase64(file);
                const preview = file.type.startsWith('image/')
                    ? createFilePreview(file)
                    : undefined;
                newFiles.push({
                    id: `${Date.now()}-${i}`,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    base64,
                    preview,
                });
            }
            catch (error) {
                console.error('Error converting file to base64:', error);
            }
        }
        setUploadedFiles((prev) => [...prev, ...newFiles]);
        // Clear the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const removeFile = (fileId) => {
        setUploadedFiles((prev) => {
            const file = prev.find((f) => f.id === fileId);
            if (file === null || file === void 0 ? void 0 : file.preview) {
                URL.revokeObjectURL(file.preview);
            }
            return prev.filter((f) => f.id !== fileId);
        });
    };
    const handleFormSubmit = async (e) => {
        await handleSubmitWithFiles(e, uploadedFiles);
        // Clear uploaded files after sending
        setUploadedFiles([]);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleFormSubmit(e);
        }
    };
    return (React.createElement(Drawer, { dismissible: false, open: isOpen, onOpenChange: handleOpenChange, direction: "right" },
        React.createElement(DrawerContent, { className: "h-full w-3/4 ml-auto", onInteractOutside: () => { } },
            React.createElement(DrawerHeader, { className: "border-b" },
                React.createElement("div", { className: "flex items-center justify-between" },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Bot, { className: "h-5 w-5 text-muted-foreground" }),
                        React.createElement(DrawerTitle, { className: "text-lg" }, "Cargomatic AI Assistant"),
                        React.createElement(DrawerDescription, { className: "sr-only" }, "This drawer provides access to the Cargomatic AI Assistant, where you can ask questions and receive operational and support help in real time."),
                        isLoading && (React.createElement("span", { className: "text-xs text-muted-foreground animate-pulse bg-blue-100 px-2 py-1 rounded" }, "typing..."))),
                    React.createElement(Button, { variant: "ghost", size: "sm", onClick: onClose, className: "h-8 w-8 p-0", "aria-label": "Close AI Assistant" },
                        React.createElement(X, { className: "h-4 w-4" })))),
            React.createElement(ScrollArea, { className: "flex-1 p-4", ref: scrollAreaRef },
                React.createElement("div", { className: "space-y-4" },
                    messages.length === 0 && (React.createElement("div", { className: "text-center text-muted-foreground py-8" },
                        React.createElement(Bot, { className: "h-12 w-12 mx-auto mb-4 text-muted-foreground/50" }),
                        React.createElement("p", { className: "text-lg font-medium mb-2" }, "Hello! I'm \"Friday\", your AI assistant"),
                        React.createElement("p", { className: "text-sm" }, "Ask me operations & support and I'll be happy to help!"))),
                    messages.map((message, index) => {
                        const csvData = detectCSV(message.content);
                        return (React.createElement("div", { key: message.id, className: `flex gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}` },
                            React.createElement("div", { className: `flex gap-2 w-3/4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}` },
                                React.createElement("div", { className: `flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs ${message.role === 'user'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-muted-foreground'}` }, message.role === 'user' ? (React.createElement(User, { className: "h-3 w-3" })) : (React.createElement(Bot, { className: "h-3 w-3" }))),
                                React.createElement("div", { className: `rounded-lg px-3 py-2 text-sm ${message.role === 'user'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-foreground'}`, style: {
                                        WebkitUserSelect: 'text',
                                        MozUserSelect: 'text',
                                        msUserSelect: 'text',
                                        userSelect: 'text',
                                    } },
                                    React.createElement("div", { className: "whitespace-pre-wrap" },
                                        React.createElement(Markdown, { remarkPlugins: [remarkGfm] }, message.content)),
                                    csvData && message.role === 'assistant' && (React.createElement("div", { className: "mt-2 flex justify-end" },
                                        React.createElement(Button, { onClick: () => handleCopyCSV(message.content), size: "sm", variant: "ghost", className: "h-6 px-2 text-xs", title: "Copy CSV data" },
                                            React.createElement(Copy, { className: "h-3 w-3 mr-1" }),
                                            "Copy CSV")))),
                                message.role === 'user' && index > messages.length - 3 && (React.createElement("div", null,
                                    React.createElement(Button, { onClick: () => retryMessage(message.content), size: "sm", variant: "ghost", disabled: isLoading, "data-testid": "retry-button" },
                                        React.createElement(Repeat, { className: "h-4 w-4" })))))));
                    }),
                    isLoading && (React.createElement("div", { className: "flex gap-2 justify-start" },
                        React.createElement("div", { className: "flex gap-2 max-w-[85%]" },
                            React.createElement("div", { className: "flex-shrink-0 w-7 h-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center" },
                                React.createElement(Bot, { className: "h-3 w-3" })),
                            React.createElement("div", { className: "rounded-lg px-3 py-2 bg-muted" },
                                React.createElement(TypingIndicator, { dotSize: "sm", color: "gray", className: "ml-1" }))))))),
            uploadedFiles.length > 0 && (React.createElement("div", { className: "px-4 py-2 border-t border-gray-200" },
                React.createElement("div", { className: "flex flex-wrap gap-2" }, uploadedFiles.map((file) => (React.createElement("div", { key: file.id, className: "flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 text-sm" },
                    React.createElement(Paperclip, { className: "h-4 w-4 text-gray-500" }),
                    React.createElement("span", { className: "truncate max-w-32" }, file.name),
                    React.createElement("span", { className: "text-xs text-gray-500" },
                        "(",
                        (file.size / 1024).toFixed(1),
                        "KB)"),
                    React.createElement(Button, { variant: "ghost", size: "sm", onClick: () => removeFile(file.id), className: "h-6 w-6 p-0" },
                        React.createElement(X, { className: "h-3 w-3" })))))))),
            React.createElement("div", { className: "p-4 border-t" },
                React.createElement("form", { onSubmit: handleFormSubmit, className: "space-y-4" },
                    React.createElement("div", { className: "relative" },
                        React.createElement(Input, { value: input, onChange: handleInputChange, onKeyDown: handleKeyDown, placeholder: "Type your message...", className: "w-full h-16 text-base px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors" }),
                        React.createElement("input", { ref: fileInputRef, type: "file", multiple: true, accept: "image/png, image/jpeg, image/jpg, application/pdf, text/plain, .csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword, .eml, .msg, message/rfc822, application/vnd.ms-outlook", onChange: handleFileUpload, className: "hidden absolute -top-9999px opacity-0 pointer-events-none", style: { position: 'absolute', left: '-9999px' } })),
                    React.createElement("div", { className: "flex items-center justify-end gap-2" },
                        React.createElement(Button, { type: "button", size: "sm", onClick: initOAuth, variant: "ghost", disabled: isLoading, title: "Init Google", className: "h-8 w-8 p-0", "data-testid": "google-social-auth" },
                            React.createElement(Mail, { className: "h-4 w-4" })),
                        React.createElement(Button, { type: "button", onClick: () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, size: "sm", variant: "ghost", disabled: isLoading, title: "Upload Files", className: "h-8 w-8 p-0", "data-testid": "upload-files" },
                            React.createElement(Paperclip, { className: "h-4 w-4" })),
                        React.createElement(Button, { type: "button", onClick: clearMessages, size: "sm", variant: "ghost", disabled: isLoading, title: "Clear conversation", className: "h-8 w-8 p-0", "data-testid": "clear-button" },
                            React.createElement(Trash, { className: "h-4 w-4" })),
                        React.createElement(Button, { type: "submit", disabled: isLoading || (!input.trim() && uploadedFiles.length === 0), size: "sm", className: "bg-green-cm hover:bg-green-dark text-white h-8 w-8 p-0", title: "Send message", "data-testid": "send-button" },
                            React.createElement(ArrowUp, { className: "h-4 w-4" }))))))));
}
/**
 * Self-contained floating AI assistant component with role check
 * Includes both the floating button and the chat overlay
 */
export function FloatingAIAssistant({ shipmentId }) {
    const [isChatOpen, setIsChatOpen] = React.useState(false);
    // Only render if user has beta-ai role
    if (!SessionStore.hasRole('beta-ai')) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { onClick: () => setIsChatOpen(true), className: "fixed bottom-6 right-6 h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-40", size: "icon" },
            React.createElement(Sparkle, { className: "h-5 w-5" })),
        React.createElement(AIChatOverlay, { isOpen: isChatOpen, onClose: () => setIsChatOpen(false), shipmentId: shipmentId })));
}
